import {
  Button,
  IframeWrapper,
  Logo,
  Spacing,
  SpacingName,
  Carousel,
} from '@doveit/lego';
import { NextPage } from 'next';
import React, { useCallback } from 'react';
import PageHead from '../client/components/page-head/PageHead';
import PageSection from '../client/components/page-section/PageSection';
import SaveJobApplication from '../client/containers/save-job-application/SaveJobApplication';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import FAQ, { FaqProps } from '../client/templates/common/faq/Faq';
import {
  HeroSection, TextAlign, Video, WrapperSize,
} from '../client/types';
import { BECOME_AGENT_INFO_AS, BECOME_AGENT_INFO_URL } from '../client/utils/links';
import * as styles from '../client/templates/become-agent/BecomeAgent.style';
import Hero from '../client/templates/common/hero/Hero';
import { getStructuredData } from '../data/structured';
import { getVideoStructuredData } from '../data/structured/video';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import { withGetServerSideError, withError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import InfoList, { InfoListProps } from '../client/components/info-list/InfoList';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import useRefCallback from '../client/hooks/useRefCallback/useRefCallback';
import {
  AgentStory as AgentStoryModel, JobPosition,
} from '../domain/graphcms';
import { JobApplicationSource } from '../domain/jobApplicationSource';
import HAccordion from '../client/components/haccordion/HAccordion';
import Markdown from '../client/components/markdown/Markdown';
import AgentStory from '../client/components/agent-story/AgentStory';
import { JobApplication } from '../services/dtos';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { AGENT_STORIES } from '../client/graphql/queries/agent';
import { JOB_POSITION_QUERY } from '../client/graphql/queries/job-offer';
import { sendJobApplicationSubmitEvent } from '../client/utils/tracking';

export const JOB_APPLICATION_STORAGE_KEY = 'job-application-form';

export interface BecomeAgentProps {
  SEO: {
    title: string,
    description: string,
  },
  hero: HeroSection,
  intro: {
    title: string,
    description: string,
    video: Video,
    cta: string,
  },
  infoSection: {
    title: string,
    items: InfoListProps['items'],
    cta: string,
  },
  valuesSection: {
    title: string,
    description: string,
    items: {
      title: string,
      description: string,
    }[],
    cta: string,
  },
  storiesSection: {
    title: string,
    items: AgentStoryModel[],
    cta: string,
  },
  faq: FaqProps,
  formSection: {
    title: string,
    description: string,
  },
  jobPosition: JobPosition,
}

export const BecomeAgent: NextPage<BecomeAgentProps> = ({
  SEO,
  hero,
  intro,
  storiesSection,
  infoSection,
  valuesSection,
  faq,
  formSection,
  jobPosition,
}) => {
  const { current, ref: formRef } = useRefCallback();

  const onSaveJobApplicationSuccess = useCallback(
    (element: string) => (jobApplication: JobApplication) => {
      sessionStorage.setItem(JOB_APPLICATION_STORAGE_KEY, JSON.stringify(jobApplication));

      sendJobApplicationSubmitEvent(
        element,
        jobApplication,
        jobPosition,
        {
          urlObject: {
            url: BECOME_AGENT_INFO_URL,
            as: BECOME_AGENT_INFO_AS,
          },
        },
      );
    },
    [jobPosition],
  );

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(
          getVideoStructuredData(intro.video),
        )}
      />
      <PageLayout>
        <Hero
          title={hero.title}
          description={hero.description}
          secondarySlot={(
            <SaveJobApplication
              jobPositionId={jobPosition.id}
              showProvince
              source={JobApplicationSource.DIVENTA_AGENTE}
              dataRef="job-application-form-hero"
              dataTracking="job-application-form-hero-submit"
              onSuccess={onSaveJobApplicationSuccess('hero')}
            />
          )}
          secondarySlotTitle={hero.formTitle}
        />
        <PageSection id="intro" padding={[0, 0, SpacingName.BIG]}>
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {intro.title}
            </PageSection.Title>
            <PageSection.Block>
              <div css={styles.grid}>
                <div css={styles.column}>
                  <PageSection.Description text={intro.description} />
                </div>
                <div css={styles.column}>
                  <div css={styles.videoWrapper}>
                    <IframeWrapper
                      title={intro.video.title}
                      src={intro.video.embedUrl}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      fit
                    />
                  </div>
                </div>
              </div>
            </PageSection.Block>
            <PageSection.Actions textAlign={TextAlign.CENTER}>
              <div css={styles.singleCTA}>
                <Button
                  label={intro.cta}
                  onClick={scrollToRef(current)}
                  expanded
                />
              </div>
            </PageSection.Actions>
          </PageSection.Wrapper>
        </PageSection>

        <PageSection data-ref="stories-section">
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {storiesSection.title}
            </PageSection.Title>
            <PageSection.Block>
              <Carousel totalElements={storiesSection.items.length}>
                <div css={styles.carouselFrame}>
                  <div css={styles.carouselInnerFrame}>
                    <Carousel.Items>
                      {storiesSection.items.map((agentStory) => (
                        <Carousel.Item key={agentStory.id}>
                          <AgentStory story={agentStory} />
                        </Carousel.Item>
                      ))}
                    </Carousel.Items>
                  </div>
                </div>
                <Spacing margin={[SpacingName.MEDIUM, 0, 0]}>
                  <Carousel.Navigation />
                </Spacing>
              </Carousel>
            </PageSection.Block>
            <PageSection.Actions textAlign={TextAlign.CENTER}>
              <div css={styles.singleCTA}>
                <Button
                  label={storiesSection.cta}
                  onClick={scrollToRef(current)}
                  expanded
                />
              </div>
            </PageSection.Actions>
          </PageSection.Wrapper>
        </PageSection>

        <PageSection data-ref="advantages-section">
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {infoSection.title}
            </PageSection.Title>
            <Spacing margin={[SpacingName.BIG, 0, 0]}>
              <InfoList
                items={infoSection.items.map((item) => ({
                  ...item,
                  picture: requireFromStaticAsset(item.picture),
                }))}
              />
            </Spacing>
            <PageSection.Actions textAlign={TextAlign.CENTER}>
              <div css={styles.singleCTA}>
                <Button
                  label={infoSection.cta}
                  onClick={scrollToRef(current)}
                  expanded
                />
              </div>
            </PageSection.Actions>
          </PageSection.Wrapper>
        </PageSection>

        <PageSection data-ref="values-section">
          <PageSection.Wrapper>
            <PageSection.Title textAlign={TextAlign.CENTER}>
              {valuesSection.title}
            </PageSection.Title>
            <PageSection.Description
              textAlign={TextAlign.CENTER}
              text={valuesSection.description}
            />
            <PageSection.Block>
              <HAccordion initialOpenBlockPosition={1}>
                {valuesSection.items.map((item) => (
                  <HAccordion.Block
                    key={item.title}
                    title={item.title}
                  >
                    <div css={styles.valueBlock}>
                      <div css={styles.valueBlockLogo}>
                        <Logo />
                      </div>
                      <div css={styles.valueBlockTitle}>
                        {item.title}
                      </div>
                      <div css={styles.valueBlockDescription}>
                        <Markdown>
                          {item.description}
                        </Markdown>
                      </div>
                    </div>
                  </HAccordion.Block>
                ))}
              </HAccordion>
            </PageSection.Block>
            <PageSection.Actions textAlign={TextAlign.CENTER}>
              <div css={styles.singleCTA}>
                <Button
                  label={valuesSection.cta}
                  onClick={scrollToRef(current)}
                  expanded
                />
              </div>
            </PageSection.Actions>
          </PageSection.Wrapper>
        </PageSection>

        <FAQ
          title={faq.title}
          description={faq.description}
          items={faq.items}
        />

        <div ref={formRef}>
          <PageSection>
            <PageSection.Wrapper>
              <PageSection.Title textAlign={TextAlign.CENTER}>
                {formSection.title}
              </PageSection.Title>
              <PageSection.Description
                textAlign={TextAlign.CENTER}
                text={formSection.description}
              />
            </PageSection.Wrapper>

            <PageSection.Wrapper maxWidth={WrapperSize.M}>
              <PageSection.Block>
                <SaveJobApplication
                  jobPositionId={jobPosition.id}
                  showProvince
                  dataRef="job-application-form-footer"
                  dataTracking="job-application-form-main-submit"
                  source={JobApplicationSource.DIVENTA_AGENTE}
                  onSuccess={onSaveJobApplicationSuccess('main')}
                />
              </PageSection.Block>
            </PageSection.Wrapper>
          </PageSection>
        </div>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<BecomeAgentProps>(async () => {
  const data = await importDynamicData('pages/become-agent.yml');
  const graphQLClient = initGraphQLClient();
  const [{ data: { agentStories } }, { data: { jobPosition } }] = await Promise.all([
    graphQLClient.query<{ agentStories: AgentStoryModel[] }>({
      query: AGENT_STORIES,
    }),
    graphQLClient.query<{ jobPosition: JobPosition }>({
      query: JOB_POSITION_QUERY,
      variables: {
        id: data.jobPositionId,
      },
    }),
  ]);

  return {
    props: {
      ...data,
      storiesSection: {
        ...data.storiesSection,
        items: agentStories,
      },
      jobPosition,
    },
  };
});

export default withError(BecomeAgent);
